/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Button,
  Typography,
} from '@mui/material';
import { TravelerBasicInfo } from '@marageti/z4-sdk/lib/people';
import {
  getFormProps,
  getFieldProps,
  getSubmitButtonProps,

  useApiClient,
  CountrySelector,
  useFetch,
} from '@marageti/z4-lib';
import LoadingButton from '@mui/lab/LoadingButton';
import { TripRequest } from '@marageti/z4-sdk/lib/travel';
import { useSnackbar } from 'notistack';

type InviteCoTravelerViewProps = {
  onClose: () => void;
  tripRequest?: TripRequest;
  tripRequestId?: string;
  onSuccess?: () => void;
};

const InviteCoTravelerView = ({
  onClose, tripRequest, tripRequestId, onSuccess,
}: InviteCoTravelerViewProps) => {
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
    reset,
  } = useForm<TravelerBasicInfo>({
    mode: 'onChange',
    defaultValues: {
      givenName: '',
      familyName: '',
      email: '',
      countryOfResidence: 'US',
    },
  });
  const { data: fetchedTripRequest } = useFetch<TripRequest>(apiClient.tripRequestsClient.getTripRequest, [tripRequestId], !!tripRequestId);
  const handleClose = () => {
    reset();
    onClose();
  };
  const tripRequestToUse = tripRequest || fetchedTripRequest;

  const onSubmit = async (inviteData: TravelerBasicInfo) => {
    if (!tripRequestToUse) return;
    try {
      await apiClient.tripRequestsClient.postTripRequestTraveler(tripRequestToUse.id, inviteData);
      enqueueSnackbar('Invite Submitted.', { variant: 'default' });
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error sending invitation:', error);
      enqueueSnackbar('Error sending invitation', { variant: 'default' });
    } finally {
      handleClose();
    }
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Invite Co-Traveler</DialogTitle>
      <form {...getFormProps(handleSubmit(onSubmit))}>
        <DialogContent sx={{ paddingTop: 0 }}>
          <Typography variant="body1">
            They will have access to the trip details and documents, and can join the conversations with travel specialists.
          </Typography>
          <Grid container spacing={0} sx={{ pt: 4 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="First Name"
                fullWidth
                required
                {...register('givenName', { required: 'Required field' })}
                {...getFieldProps('givenName', errors)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Last Name"
                fullWidth
                required
                {...register('familyName', { required: 'Required field' })}
                {...getFieldProps('familyName', errors)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email Address"
                type="email"
                fullWidth
                required
                {...getFieldProps('email', errors)}
                {...register('email', {
                  required: 'Required field',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                  validate: (value) => {
                    if (tripRequestToUse?.travelers?.some((traveler) => traveler.email === value)) {
                      return 'This email has already been invited.';
                    }
                    return true;
                  },
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="countryOfResidence"
                control={control}
                render={({ field }) => (
                  <CountrySelector
                    {...field}
                    {...getFieldProps('countryOfResidence', errors)}
                    label="Country of Residence"
                    onChange={(_: any, newValue: any) => {
                      field.onChange(newValue?.value || null);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            type="submit"
            variant="contained"
            {...getSubmitButtonProps(!!isSubmitting, !!isValid)}
          >
            Send Invite
          </LoadingButton>
          <Button variant="outlined" sx={{ borderColor: 'common.black25' }} onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default InviteCoTravelerView;
